import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Plus, Hash, AlertCircle, Clock } from 'lucide-react';
import { useChAuth } from '../contexts/ChAuthContext';
import { useModal } from '../../contexts/ModalContext';
import { chProjectService } from '../services';
import { chPostService } from '../services/postService';
import { AiDailySummaryCard } from '../components/AiDailySummaryCard';
import { isChStaffRole, type ChProject } from '../types';

export function ChDashboard() {
  const { user } = useChAuth();
  const { toast } = useModal();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ChProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientPending, setClientPending] = useState<{ projectName: string; projectSlug: string; awaiting: number; changes: number }[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const data = await chProjectService.getAll();
        const active = data.filter(p => !p.archived);
        setProjects(active);

        if (user?.role === 'admin' || user?.role === 'project_manager') {
          const pendingData: typeof clientPending = [];
          for (const p of active.slice(0, 10)) {
            try {
              const posts = await chPostService.getByProject(p.id);
              const awaiting = posts.filter(post => post.status === 'em_revisao' && post.client_decision !== 'approved' && post.client_decision !== 'changes_requested').length;
              const changes = posts.filter(post => post.client_decision === 'changes_requested').length;
              if (awaiting > 0 || changes > 0) {
                pendingData.push({ projectName: p.name, projectSlug: p.slug, awaiting, changes });
              }
            } catch {
              // skip
            }
          }
          setClientPending(pendingData);
        }
      } catch {
        toast.error('Erro ao carregar os projetos');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto ch-animate-in">
      <div className="mb-8">
        <h1 className="ch-heading ch-heading-xl mb-1">
          {greeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-gray-500 text-sm">
          Gestão de conteúdo e aprovações
        </p>
      </div>

      {/* Client pending widget */}
      {clientPending.length > 0 && (
        <div className="mb-8 ch-card border-l-4 border-l-[var(--ch-magenta)]">
          <h3 className="text-sm font-bold text-[var(--ch-ink)] flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-[var(--ch-magenta)]" />
            Pendentes do cliente
          </h3>
          <div className="space-y-2">
            {clientPending.map(item => (
              <button
                key={item.projectSlug}
                onClick={() => navigate(`/contenthub/projetos/${item.projectSlug}`)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="text-sm font-medium text-[var(--ch-ink)]">{item.projectName}</span>
                <div className="flex items-center gap-3">
                  {item.awaiting > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                      <Clock className="w-3 h-3" />
                      {item.awaiting} a aguardar
                    </span>
                  )}
                  {item.changes > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ch-magenta)]">
                      <AlertCircle className="w-3 h-3" />
                      {item.changes} alterações
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI Daily Summary */}
      {(user?.role === 'admin' || user?.role === 'project_manager') && (
        <div className="mb-8">
          <AiDailySummaryCard />
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="ch-heading ch-heading-sm text-gray-500">Projetos</h2>
        {isChStaffRole(user?.role) && (
          <button
            onClick={() => navigate('/contenthub/projetos/novo')}
            className="ch-btn ch-btn-primary ch-btn-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Novo projeto
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="ch-card animate-pulse">
              <div className="h-5 w-32 bg-gray-200 rounded mb-3" />
              <div className="h-3 w-20 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="ch-empty">
          <FolderKanban className="ch-empty-icon" />
          <p className="text-gray-500 font-medium mb-1">Sem projetos</p>
          <p className="text-sm text-gray-400">Cria o primeiro projeto para começar.</p>
          {isChStaffRole(user?.role) && (
            <button
              onClick={() => navigate('/contenthub/projetos/novo')}
              className="ch-btn ch-btn-primary mt-4"
            >
              <Plus className="w-4 h-4" />
              Criar projeto
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(project => (
            <button
              key={project.id}
              onClick={() => navigate(`/contenthub/projetos/${project.slug}`)}
              className="ch-card-dark text-left group transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-[var(--ch-lime)]" />
                </div>
              </div>
              <h3 className="font-semibold text-white text-base mb-1 group-hover:text-[var(--ch-lime)] transition-colors">
                {project.name}
              </h3>
              {project.workcode && (
                <span className="inline-flex items-center gap-1 text-xs text-white/40 font-mono">
                  <Hash className="w-3 h-3" />
                  {project.workcode}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
